import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import setPageMeta from '../../utils/setPageMeta';
import { PAGES } from '../../seo/site';
import CircuitBackground from '../corporate-landing-page/components/CircuitBackground';
import MainframeNavbar from '../corporate-landing-page/components/MainframeNavbar';
import MainframeFooter from '../corporate-landing-page/components/MainframeFooter';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProgressIndicator from './components/ProgressIndicator';
import CompanyInformationForm from './components/CompanyInformationForm';
import CurrentSystemsForm from './components/CurrentSystemsForm';
import PainPointsForm from './components/PainPointsForm';
import ContactPreferencesForm from './components/ContactPreferencesForm';
import SummaryPanel from './components/SummaryPanel';

const TRUST_CARDS = [
  { icon: 'Award', title: 'Experiencia Comprobada', description: 'Empresas transformadas exitosamente' },
  { icon: 'Target', title: 'Enfoque Personalizado', description: 'Soluciones adaptadas a su industria y tamaño' },
  { icon: 'Zap', title: 'Resultados Rápidos', description: 'Recomendaciones implementables en 30 días' },
];

// Arranca el circuito de esta página en la sección de confianza (fondo
// negro) -- entra por la izquierda y baja recto todo el borde, listo para
// entregarle el trazo a la sección del formulario.
const TrustCircuit = () => (
  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
    <div className="absolute left-4 top-0 bottom-0 w-[1.5px] bg-[#8B5CF6] opacity-40" />
  </div>
);

// Recibe el trazo violeta y, al entrar a fondo blanco, vuelve al índigo
// claro. Baja recto por el borde izquierdo y cruza al fondo (left: 38%)
// para entregarle el trazo al Footer en ese mismo punto.
const FormSectionCircuit = () => (
  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
    <div className="absolute left-4 top-0 bottom-0 w-[1.5px] bg-[#C7D2FE] opacity-70" />
    <div className="absolute left-4 bottom-0 w-[calc(38%-16px)] h-[1.5px] bg-[#C7D2FE] opacity-70" />
    <div className="absolute left-[10px] bottom-0 w-[11px] h-[11px] rounded-full bg-[#A5B4FC] opacity-90" />
  </div>
);

// Mini autodiagnóstico interactivo: el visitante ajusta cuántas horas
// pierde a la semana en tareas manuales y ve al instante cuánto podría
// recuperar automatizando -- reemplaza la imagen suelta con algo que
// engancha y conecta temáticamente con el resto de la página.
const QuickDiagnosticWidget = () => {
  const [hours, setHours] = useState(8);
  const monthly = hours * 4;
  const yearly = monthly * 12;

  const scrollToForm = () => {
    document.getElementById('diagnostico-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="relative mx-auto w-full max-w-sm rounded-2xl border border-black/10 p-6 sm:p-8">
      <p className="text-black/40 text-[11px] font-semibold uppercase tracking-[0.1em] mb-3">
        Autodiagnóstico rápido
      </p>
      <p className="text-black text-[16px] sm:text-[17px] font-medium leading-snug mb-6">
        ¿Cuántas horas a la semana pierdes en tareas manuales?
      </p>

      <div className="flex items-center justify-center gap-5 mb-6">
        <button
          type="button"
          onClick={() => setHours((h) => Math.max(1, h - 1))}
          aria-label="Restar hora"
          className="w-10 h-10 rounded-full border border-black/15 text-black text-lg flex items-center justify-center hover:bg-black/5 transition-colors"
        >
          −
        </button>
        <span className="text-black text-[40px] font-semibold w-16 text-center tabular-nums">{hours}</span>
        <button
          type="button"
          onClick={() => setHours((h) => Math.min(40, h + 1))}
          aria-label="Sumar hora"
          className="w-10 h-10 rounded-full border border-black/15 text-black text-lg flex items-center justify-center hover:bg-black/5 transition-colors"
        >
          +
        </button>
      </div>

      <div className="rounded-xl bg-black p-4 text-center mb-6">
        <p className="text-white/50 text-[12px] mb-1">Eso son aproximadamente</p>
        <p className="text-[#A78BFA] text-[24px] font-semibold">{monthly} horas al mes</p>
        <p className="text-white/50 text-[12px]">({yearly} horas al año) que podrías recuperar automatizando</p>
      </div>

      <button
        type="button"
        onClick={scrollToForm}
        className="w-full inline-flex items-center justify-center gap-2 bg-black text-white text-[14px] font-medium px-6 py-3 rounded-full hover:opacity-80 transition-opacity"
      >
        Empezar mi diagnóstico
        <span aria-hidden="true">→</span>
      </button>
    </div>
  );
};

const DiagnosticConsultationRequest = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const steps = [
    {
      id: 'company',
      title: 'Información de la Empresa',
      component: CompanyInformationForm
    },
    {
      id: 'systems',
      title: 'Sistemas Actuales',
      component: CurrentSystemsForm
    },
    {
      id: 'painpoints',
      title: 'Barreras para el Crecimiento',
      component: PainPointsForm
    },
    {
      id: 'contact',
      title: 'Preferencias de Contacto',
      component: ContactPreferencesForm
    }
  ];

  useEffect(() => {
    setPageMeta(PAGES.diagnostic);

    // Load saved draft from localStorage
    const savedDraft = localStorage.getItem('diagnostic-consultation-draft');
    if (savedDraft) {
      try {
        const parsedData = JSON.parse(savedDraft);
        setFormData(parsedData);
      } catch (error) {
        console.error('Error loading saved draft:', error);
      }
    }
  }, []);

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};

    switch (stepIndex) {
      case 0: // Company Information
        if (!formData.companyName?.trim()) {
          newErrors.companyName = 'El nombre de la empresa es requerido';
        }
        if (!formData.industry) {
          newErrors.industry = 'La industria es requerida';
        }
        if (!formData.employeeCount) {
          newErrors.employeeCount = 'El número de empleados es requerido';
        }
        if (!formData.country?.trim()) {
          newErrors.country = 'El país es requerido';
        }
        break;

      case 1: // Current Systems
        if (!formData.currentSoftware?.length) {
          newErrors.currentSoftware = 'Seleccione al menos un sistema actual';
        }
        break;

      case 2: // Pain Points
        if (!formData.painPoints?.length) {
          newErrors.painPoints = 'Seleccione al menos un punto de dolor';
        }
        if (!formData.urgency) {
          newErrors.urgency = 'La urgencia del proyecto es requerida';
        }
        break;

      case 3: // Contact Preferences
        if (!formData.contactName?.trim()) {
          newErrors.contactName = 'El nombre de contacto es requerido';
        }
        if (!formData.jobTitle?.trim()) {
          newErrors.jobTitle = 'El cargo es requerido';
        }
        if (!formData.email?.trim()) {
          newErrors.email = 'El correo electrónico es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Ingrese un correo electrónico válido';
        }
        if (!formData.phone?.trim()) {
          newErrors.phone = 'El teléfono es requerido';
        }
        if (!formData.decisionRole) {
          newErrors.decisionRole = 'El rol en la decisión es requerido';
        }
        if (!formData.acceptTerms) {
          newErrors.acceptTerms = 'Debe aceptar los términos y condiciones';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSaveDraft = () => {
    localStorage.setItem('diagnostic-consultation-draft', JSON.stringify(formData));
    // Show success message
    const event = new CustomEvent('showToast', {
      detail: {
        type: 'success',
        message: 'Borrador guardado exitosamente'
      }
    });
    window.dispatchEvent(event);
  };

  const handleRequestCallback = () => {
    // Mock callback request
    const event = new CustomEvent('showToast', {
      detail: {
        type: 'info',
        message: 'Solicitud de llamada enviada. Nos contactaremos en las próximas 24 horas.'
      }
    });
    window.dispatchEvent(event);
  };

  const handleScheduleConsultation = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Clear saved draft
      localStorage.removeItem('diagnostic-consultation-draft');

      setShowSuccessModal(true);
    } catch (error) {
      const event = new CustomEvent('showToast', {
        detail: {
          type: 'error',
          message: 'Error al programar la consultoría. Intente nuevamente.'
        }
      });
      window.dispatchEvent(event);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/');
  };

  const CurrentStepComponent = steps[currentStep]?.component;

  return (
    <div className="min-h-screen bg-white text-black overflow-x-hidden force-light-mode" style={{ fontFamily: 'var(--font-body)' }}>
      <CircuitBackground />
      <MainframeNavbar />

      {/* Hero */}
      <section className="relative z-[1] pt-32 sm:pt-40 pb-16 sm:pb-20 px-5 sm:px-8 md:px-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          <div>
            <h1
              style={{ fontSize: 'clamp(30px, 5vw, 52px)', lineHeight: 1.15, fontWeight: 500, letterSpacing: '-0.01em' }}
              className="text-black mb-6"
            >
              Diagnóstico gratuito de automatización
            </h1>
            <p
              className="text-black/60 mb-8 max-w-lg"
              style={{ fontSize: 'clamp(15px, 2vw, 18px)', lineHeight: 1.5, fontWeight: 400 }}
            >
              Obtén una evaluación personalizada de tus flujos de trabajo y descubre cómo la
              Inteligencia Artificial y los sistemas autónomos pueden escalar tu operación.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] sm:text-[14px] text-black/60">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} className="text-[#7C3AED]" />
                <span>2-4 horas de consultoría</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Shield" size={16} className="text-[#7C3AED]" />
                <span>100% gratuito</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Users" size={16} className="text-[#7C3AED]" />
                <span>Expertos certificados</span>
              </div>
            </div>
          </div>

          <QuickDiagnosticWidget />
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="relative z-[1] bg-black px-5 sm:px-8 md:px-10 py-16 sm:py-20 overflow-hidden">
        <TrustCircuit />
        <div className="relative z-10 max-w-4xl mx-auto">
          <h2
            style={{ fontSize: 'clamp(24px, 3.6vw, 36px)', lineHeight: 1.15, fontWeight: 500, letterSpacing: '-0.01em' }}
            className="text-white text-center mb-12 sm:mb-14"
          >
            ¿Por qué elegir nuestra auditoría?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {TRUST_CARDS.map((card) => (
              <div key={card.title} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
                  <Icon name={card.icon} size={24} className="text-[#A78BFA]" />
                </div>
                <h4 className="text-white text-[16px] sm:text-[17px] font-semibold mb-2">{card.title}</h4>
                <p className="text-white/60 text-[14px] sm:text-[15px]">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="diagnostico-form" className="relative z-[1] bg-white px-5 sm:px-8 md:px-10 py-16 sm:py-20 overflow-hidden">
        <FormSectionCircuit />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form Content */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8">
                <ProgressIndicator
                  currentStep={currentStep}
                  totalSteps={steps.length}
                  steps={steps}
                />

                {CurrentStepComponent && (
                  <CurrentStepComponent
                    formData={formData}
                    updateFormData={updateFormData}
                    errors={errors}
                  />
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-black/10">
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={currentStep === 0}
                    iconName="ChevronLeft"
                    iconPosition="left"
                    className="border-black/20 text-black hover:bg-black/5"
                  >
                    Anterior
                  </Button>

                  {currentStep < steps.length - 1 ? (
                    <Button
                      variant="default"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                      className="bg-black hover:opacity-80 text-white font-medium"
                    >
                      Siguiente
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      onClick={handleScheduleConsultation}
                      loading={isSubmitting}
                      iconName="Calendar"
                      iconPosition="left"
                      className="bg-black hover:opacity-80 text-white font-medium"
                    >
                      Programar Consultoría
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Panel */}
            <div className="lg:col-span-1">
              <SummaryPanel
                formData={formData}
                currentStep={currentStep}
                onScheduleConsultation={handleScheduleConsultation}
                onSaveDraft={handleSaveDraft}
                onRequestCallback={handleRequestCallback}
              />
            </div>
          </div>
        </div>
      </section>

      <MainframeFooter />

      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white border border-black/10 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} color="white" />
              </div>
              <h3 className="text-xl font-semibold text-black mb-2">
                ¡Consultoría Programada!
              </h3>
              <p className="text-black/60 mb-6">
                Hemos recibido su solicitud. Nuestro equipo se contactará con usted en las próximas 24 horas para confirmar la fecha y hora de su consultoría diagnóstica gratuita.
              </p>
              <div className="space-y-3">
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleSuccessModalClose}
                  className="bg-black hover:opacity-80 text-white font-medium"
                >
                  Continuar
                </Button>
                <p className="text-xs text-black/50">
                  Revise su correo electrónico para más detalles
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Floating Actions */}
      <div className="fixed bottom-6 right-6 z-30 lg:hidden">
        <div className="flex flex-col space-y-3">
          <Button
            variant="outline"
            size="icon"
            onClick={handleSaveDraft}
            className="w-12 h-12 rounded-full shadow-lg border-black/20"
          >
            <Icon name="Save" size={20} />
          </Button>
          <Button
            variant="default"
            size="icon"
            onClick={currentStep < steps.length - 1 ? handleNext : handleScheduleConsultation}
            className="w-12 h-12 rounded-full bg-black hover:opacity-80 text-white shadow-lg"
            loading={isSubmitting}
          >
            <Icon name={currentStep < steps.length - 1 ? "ChevronRight" : "Calendar"} size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticConsultationRequest;
